import {
  queryOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";
import { medusa } from "#/lib/medusa";
import type {
  PickupHub,
  PickupHubsResponse,
  PickupReservation,
  PickupReservationInput,
  PickupSlot,
  PickupSlotsResponse,
} from "#/lib/medusa-types/pickup";

const hubSchema = z.string();

const hubsResponseSchema = z.object({
  hubs: z.array(
    z.object({
      key: z.string(),
      name: z.string(),
      label: z.string(),
      opens_at: z.string(),
      closes_at: z.string(),
      hp_allowed: z.boolean(),
    }) satisfies z.ZodType<PickupHub>,
  ),
}) satisfies z.ZodType<PickupHubsResponse>;

const slotSchema = z.object({
  time_slot_id: z.string(),
  name: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  booked: z.number(),
  remaining: z.number(),
}) satisfies z.ZodType<PickupSlot>;

const slotsResponseSchema = z.object({
  hub: hubSchema,
  pickup_date: z.string(),
  max_per_slot: z.number(),
  slots: z.array(slotSchema),
}) satisfies z.ZodType<PickupSlotsResponse>;

const reservationSchema = z.object({
  id: z.string(),
  cart_id: z.string().nullable(),
  order_id: z.string().nullable(),
  hub: hubSchema,
  pickup_date: z.string(),
  time_slot: z.object({
    id: z.string(),
    name: z.string(),
    start_time: z.string(),
    end_time: z.string(),
  }),
}) satisfies z.ZodType<PickupReservation>;

const reserveResponseSchema = z.object({ reservation: reservationSchema });

const releaseResponseSchema = z.object({ released: z.boolean() });

const PICKUP_CONFLICT_MESSAGE =
  "Your pickup slot is still being saved. Please try again in a moment.";

export class PickupReservationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PickupReservationError";
  }
}

function toPickupReservationError(err: unknown): PickupReservationError {
  if (err instanceof Error && Reflect.get(err, "status") === 409) {
    return new PickupReservationError(PICKUP_CONFLICT_MESSAGE);
  }
  return new PickupReservationError(
    err instanceof Error && err.message
      ? err.message
      : "We couldn't hold that pickup slot. Please try again.",
  );
}

export async function reservePickupSlot(
  input: PickupReservationInput,
): Promise<PickupReservation> {
  let raw: unknown;
  try {
    raw = await medusa.client.fetch<unknown>("/store/pickup-reservations", {
      method: "POST",
      body: input,
    });
  } catch (err) {
    throw toPickupReservationError(err);
  }
  return reserveResponseSchema.parse(raw).reservation;
}

export async function releasePickupSlot(cartId: string): Promise<boolean> {
  const raw = await medusa.client.fetch<unknown>("/store/pickup-reservations", {
    method: "DELETE",
    query: { cart_id: cartId },
  });
  return releaseResponseSchema.parse(raw).released;
}

export const pickupKeys = {
  all: ["storefront", "pickup"] as const,
  slots: (hub: string | null, pickupDate: string) =>
    ["storefront", "pickup", "slots", hub, pickupDate] as const,
  hubs: ["storefront", "pickup-hubs"] as const,
};

export const pickupQueries = {
  hubs: () =>
    queryOptions({
      queryKey: pickupKeys.hubs,
      queryFn: async (): Promise<PickupHub[]> => {
        const raw = await medusa.client.fetch<unknown>("/store/pickup-hubs");
        return hubsResponseSchema.parse(raw).hubs;
      },
    }),
  slots: (hub: string | null, pickupDate: string) =>
    queryOptions({
      queryKey: pickupKeys.slots(hub, pickupDate),
      queryFn: async (): Promise<PickupSlotsResponse> => {
        const raw = await medusa.client.fetch<unknown>("/store/pickup-slots", {
          query: { hub, pickup_date: pickupDate },
        });
        return slotsResponseSchema.parse(raw);
      },
      enabled: hub !== null && pickupDate.length > 0,
    }),
};

export function useReservePickupSlot(): UseMutationResult<
  PickupReservation,
  Error,
  PickupReservationInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reservePickupSlot,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: pickupKeys.all }),
  });
}

export function useReleasePickupSlot(): UseMutationResult<
  boolean,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: releasePickupSlot,
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: pickupKeys.all }),
  });
}
